/**
 * payment-offers 域 Zod 共享契约 —— ADM-PAY-S005 运营支付活动 offer 管理
 *
 * 契约源：dev-docs `02-API契约/A8-运营支付.md` §6 + `03-数据字典/70-产品配置.md` §3.4（app_payment_offers）。
 *
 * 设计：
 * - app_payment_offers = C 端 own 镜像表（schema/shared/app-payment-offers.ts），admin 拥运营写；活动实例
 *   承载有效期窗口（starts_at/ends_at 必填、无无限期）+ 资格（all/new_user）+ 复用既有 scenario 通道配置。
 * - scenario 取 payments.ts `PAYMENT_SCENARIOS` 去掉 `operational_popup` / `reading_pass` / `novel_purchase` 的**三值活动子集**（A8 §6 / §3 offer 列）。
 * - **is_active 不在 Create/Update 契约**（安全收窄，同 product_metadata）：创建即 active，启停经
 *   POST /:id/activate|deactivate（offer 全写 operator+ mfa、不 root/admin 收窄——offer 停/删危害=失效入口
 *   死链，reverse 护栏 + C 端优雅降级，无 product_metadata 的 paid-but-nothing 面）。
 * - scenario / app_id 创建后不可改（改 scenario = 换活动 → 建新 offer）；Update .strict() 误传一律 422。
 * - **无 resurrection**：app_payment_offers 无 (app_id, scenario) 唯一约束（同 scenario 可并存多窗口活动）→
 *   create 恒为纯 INSERT（新 UUID v7），无 ON CONFLICT / 复活分支（区别于 product_metadata 的 resurrection upsert）。
 */

import { z } from 'zod';
import { PAYMENT_SCENARIOS } from './payments';

/**
 * offer 可用的 scenario 三值活动子集（A8 §6 / §6.6）：`PAYMENT_SCENARIOS` 去掉
 * `operational_popup` / `reading_pass` / `novel_purchase`。
 *
 * ⚠️ **排除项必须显式写出** —— 本常量从 `PAYMENT_SCENARIOS` filter 出来，闭集每扩一个值就会被
 * 静默吸进活动子集。契约 §3「offer 活动子集」列：`reading_pass` / `novel_purchase` 是常驻能力入口、
 * `operational_popup` 走弹窗 scene，三者均不进本子集。
 */
export const PAYMENT_OFFER_SCENARIOS = PAYMENT_SCENARIOS.filter(
  (
    s,
  ): s is Exclude<
    (typeof PAYMENT_SCENARIOS)[number],
    'operational_popup' | 'reading_pass' | 'novel_purchase'
  > => s !== 'operational_popup' && s !== 'reading_pass' && s !== 'novel_purchase',
);
export const PaymentOfferScenarioSchema = z.enum(PAYMENT_OFFER_SCENARIOS as [string, ...string[]]);
export type PaymentOfferScenario = (typeof PAYMENT_OFFER_SCENARIOS)[number];

/** 资格受众闭集（镜像 app-payment-offers.eligibility 列闭集，route-Zod-only）：all / new_user */
export const OFFER_ELIGIBILITIES = ['all', 'new_user'] as const;
export const OfferEligibilitySchema = z.enum(OFFER_ELIGIBILITIES);
export type OfferEligibility = z.infer<typeof OfferEligibilitySchema>;

/**
 * 活动有效期窗口一致性（ends_at 必须严格晚于 starts_at，半开窗 [starts_at, ends_at)）。
 * 纯函数 → Create superRefine（两值都在 body）与 **update-service**（部分更新只传一端时用已存值 + 新值合并后校验）
 * 共用——因 Update 部分更新时另一端不在 body，Zod 层无法独立判（mirror product_metadata 的 assertProductTypeConsistency）。
 * 返回错误信息（不一致）或 null（一致）。与镜像 schema 的 DB CHECK `app_payment_offers_ends_after_starts` 双层防御。
 */
export function assertOfferTimeWindow(startsAt: string, endsAt: string): string | null {
  return new Date(endsAt).getTime() > new Date(startsAt).getTime()
    ? null
    : 'ends_at must be strictly after starts_at';
}

/**
 * 创建运营支付活动 offer。scenario 复用既有支付页通道配置（不为活动新建支付页）；窗口必填、无无限期。
 * eligibility 默认 all。is_active 不在契约（创建即 active）。
 * 时间用 `z.string().datetime()`（zod 4 仅接受 UTC `Z` 结尾形态、拒数字 offset，web 端须 new Date().toISOString()）。
 */
export const CreatePaymentOfferRequestSchema = z
  .object({
    app_id: z.string().uuid(),
    /**
     * 活动复用的支付场景（A8 §6：`app_payment_methods.scenario` 4 值闭集去掉 `operational_popup` 的
     * **三值活动子集**——运营弹窗是被动触发的 paywall placement、不是活动页，指向它的 offer 是死配置）。
     * 创建后不可改（改 scenario = 换活动 → 建新 offer）。
     */
    scenario: PaymentOfferScenarioSchema,
    /** 活动开始时间（ISO 8601 带时区 UTC `Z`）*/
    starts_at: z.string().datetime(),
    /** 活动结束时间（ISO 8601 带时区 UTC `Z`）；必须严格晚于 starts_at（superRefine + DB CHECK 双层）*/
    ends_at: z.string().datetime(),
    eligibility: OfferEligibilitySchema.default('all'),
    // is_active 不在 Create/Update 契约（安全收窄）：新活动创建即上架（service 默认 is_active=true）；
    // 启停经 POST /:id/activate|deactivate（offer 全写 operator+ mfa，不 root/admin 收窄）。误传 is_active 即 422（.strict()）。
  })
  // .strict()：拒未知键——驼峰 typo / 已移出字段（is_active / scenario 拼写）会被静默 strip 后以默认值建行；
  // strict 让 typo 直接 422 而非静默默认（同 product_metadata Create）。
  .strict()
  .superRefine((v, ctx) => {
    const err = assertOfferTimeWindow(v.starts_at, v.ends_at);
    if (err) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: err, path: ['ends_at'] });
    }
  });
export type CreatePaymentOfferRequest = z.infer<typeof CreatePaymentOfferRequestSchema>;

/**
 * 更新运营支付活动 offer 的窗口 / 资格（任意子集，≥1 字段）。
 * **.strict()**：拒一切未声明键——scenario / app_id / id / is_active 等身份/不可变/收窄字段误传一律 422，
 * 而非静默 strip（fail-open 会让运营误以为改了 scenario）。is_active 启停经独立 activate/deactivate。
 * ends_at > starts_at 跨字段一致性须由 service 以「合并已存行 + 新值」调 assertOfferTimeWindow 校验
 * （Update 部分更新时另一端不在 body，Zod 层无法独立判）。
 */
export const UpdatePaymentOfferRequestSchema = z
  .object({
    starts_at: z.string().datetime().optional(),
    ends_at: z.string().datetime().optional(),
    eligibility: OfferEligibilitySchema.optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length >= 1, { message: 'at least one field required' });
export type UpdatePaymentOfferRequest = z.infer<typeof UpdatePaymentOfferRequestSchema>;

/**
 * GET /payment-offers 列表 query（offset 分页 page/limit 走 server lib/offset-page；本 schema 仅校验
 * 可选 is_active 过滤）。query 值是字符串 'true'/'false'——用 enum + transform 转 boolean（不用 z.coerce.boolean，
 * 它对 'false' 也返 true）。
 */
export const ListPaymentOffersQuerySchema = z.object({
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type ListPaymentOffersQuery = z.infer<typeof ListPaymentOffersQuerySchema>;

/**
 * offer 反向引用条目（admin 只读派生）：该 offer_id 仍被哪些 active 外部入口携带。
 * kind 闭集 {banner_slide, push_task}（= reverse 护栏 PAY_OFFER_REFERENCED details 同形态，server lib/offer-references）；
 * 运营据 id 在对应域（Banner 轮播 / 推送任务列表）定位引用方，停 / 删被拒时知道先下架谁。
 */
export type OfferReference = {
  kind: 'banner_slide' | 'push_task';
  id: string;
};

/**
 * 运营支付活动 offer 响应 DTO（DB 列名 → API 字段；timestamptz → ISO 8601 string；不含软删行）。
 * **含 scenario**（admin 管理端返回——区别于 C 端 GET /payment/offers/:offer_id/methods 对客户端隐藏内部 scenario，
 * §3.4；admin 运营需见 scenario 寻址维度）。`id` = 对外 :offer_id（路径参数 / banner·push 引用键）。
 *
 * **references**：admin 只读派生（非 DB 列、非 C 端契约字段）——列表端点 GET /payment-offers 实时扫
 * active banner_slides / 未投递 topup_promo push_tasks 算出该 offer_id 仍被哪些活引用携带，供列表展示「被引用」
 * 计数 chip + 提示「停 / 删前先下架谁」。单条 GET / 写端点不附该字段（仅列表派生），故 optional。
 */
export type PaymentOfferResponse = {
  id: string;
  app_id: string;
  scenario: PaymentOfferScenario;
  starts_at: string;
  ends_at: string;
  eligibility: OfferEligibility;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  references?: OfferReference[];
};
